import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { formatDate, hasTextContent, stripHtml, getContrastColor } from '../utils';
import Modal from './Modal';
import Icon from './Icon';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const normChapter = (c) =>
  typeof c === 'string' ? { name: c } : (c && typeof c === 'object' ? c : { name: '' });

function chapterAnchor(name) {
  if (!name) return 'chapter-uncategorized';
  return 'chapter-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function luminance(hex) {
  if (!hex || hex.length < 7) return 0;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrastText(bgHex) {
  if (!bgHex || !bgHex.startsWith('#')) return null;
  return luminance(bgHex) > 0.179 ? '#1a1a1a' : '#ffffff';
}

function calcReadingTime(events) {
  const words = events.reduce((sum, ev) => {
    const text = stripHtml(ev.description || '');
    return sum + (text ? text.split(/\s+/).filter(Boolean).length : 0);
  }, 0);
  if (words < 60) return '< 1 min read';
  return `~${Math.ceil(words / 200)} min read`;
}

const safeSlug = (title) =>
  (title || 'timeline').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'timeline';

// ─── STORYLINE TIMELINE ───────────────────────────────────────────────────────
//
//  Props:
//    events              {array}    event objects (each may have .chapter string)
//    colors              {object}   { primary, secondary, tertiary, chapterColorTinting? }
//    onEventClick        {function} called with clicked event object
//    manualOrder         {boolean}
//    chapters            {array}    ordered chapter objects OR strings from tl.chapters
//    timeline            {object}   full timeline (for export title/id/privacy)
//    isEmbed             {boolean}  embed iframe context — hides toolbar, sidebar, share buttons
//    canEdit             {boolean}  owner/editor — shows add-event buttons, always-visible share
//    onAddEventToChapter {function} (chapterName) => void — opens EventForm pre-filled

const StorylineTimeline = ({
  events = [],
  colors,
  onEventClick,
  manualOrder = false,
  chapters = [],
  timeline = null,
  isEmbed = false,
  canEdit = false,
  onAddEventToChapter = null,
}) => {
  // ── Normalize chapters to objects ─────────────────────────────────────────
  const normalizedChapters = useMemo(
    () => chapters.map(normChapter).filter((c) => c.name),
    [chapters]
  );

  // ── Sort events ───────────────────────────────────────────────────────────
  const sorted = useMemo(
    () =>
      [...events].sort((a, b) => {
        if (manualOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        const d = (a.startDate || "").localeCompare(b.startDate || "");
        return d !== 0 ? d : (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      }),
    [events, manualOrder]
  );

  // ── Build ordered chapter list (includes empty chapters for editor/viewer) ─
  const { orderedChapters, byChapter } = useMemo(() => {
    const seen = new Set();
    const ordered = [];

    // All chapters from prop (in prop order, even if empty)
    normalizedChapters.forEach((ch) => {
      ordered.push(ch);
      seen.add(ch.name);
    });

    // Any event chapters not covered by the prop
    sorted.forEach((ev) => {
      if (ev.chapter && !seen.has(ev.chapter)) {
        ordered.push({ name: ev.chapter });
        seen.add(ev.chapter);
      }
    });

    // Uncategorized bucket
    if (sorted.some((ev) => !ev.chapter)) {
      ordered.push({ name: null });
    }

    const by = new Map();
    ordered.forEach((ch) => by.set(ch.name, []));
    sorted.forEach((ev) => {
      const key = ev.chapter || null;
      if (by.has(key)) by.get(key).push(ev);
    });

    return { orderedChapters: ordered, byChapter: by };
  }, [normalizedChapters, sorted]);

  // ── All state (hooks must precede any conditional returns) ────────────────
  const [collapsed, setCollapsed] = useState({});
  const [activeChapterKey, setActiveChapterKey] = useState(null);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [copiedChapter, setCopiedChapter] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [embedWidth, setEmbedWidth] = useState('100%');
  const [embedHeight, setEmbedHeight] = useState('800');
  const [embedResp, setEmbedResp] = useState(false);
  const [embedChapterTab, setEmbedChapterTab] = useState('snippet');
  const [embedChapterSel, setEmbedChapterSel] = useState('');
  const [revealedKeys, setRevealedKeys] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [windowWidth, setWindowWidth] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth : 1024)
  );

  // ── Refs ──────────────────────────────────────────────────────────────────
  const containerRef = useRef(null);
  const chapterRefs = useRef({});
  const visMapRef = useRef(new Map());

  // ── Window resize ─────────────────────────────────────────────────────────
  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h, { passive: true });
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── URL hash navigation on mount ──────────────────────────────────────────
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const id = hash.slice(1);
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // ── Chapter reveal IntersectionObserver ──────────────────────────────────
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Immediately reveal above-the-fold chapters
    const initial = new Set();
    orderedChapters.forEach((ch) => {
      const key = ch.name ?? '__uncategorized__';
      const el = chapterRefs.current[key];
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) initial.add(key);
      }
    });

    if (prefersReduced || isEmbed) {
      // Reveal everything immediately, no animation
      setRevealedKeys(new Set(orderedChapters.map((ch) => ch.name ?? '__uncategorized__')));
      return;
    }

    if (initial.size > 0) setRevealedKeys(initial);

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const key = entry.target.dataset.chapterKey;
            setRevealedKeys((prev) => {
              if (prev.has(key)) return prev;
              return new Set([...prev, key]);
            });
          }
        });
      },
      { threshold: 0.05 }
    );

    orderedChapters.forEach((ch) => {
      const key = ch.name ?? '__uncategorized__';
      const el = chapterRefs.current[key];
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, [orderedChapters, isEmbed]);

  // ── Active chapter IntersectionObserver (sidebar highlight) ──────────────
  useEffect(() => {
    if (isEmbed || orderedChapters.length < 2) return;

    visMapRef.current = new Map();

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visMapRef.current.set(entry.target.dataset.chapterKey, {
            isIntersecting: entry.isIntersecting,
            top: entry.boundingClientRect.top,
          });
        });
        // Topmost intersecting chapter wins
        let best = null;
        let bestTop = Infinity;
        visMapRef.current.forEach((v, key) => {
          if (v.isIntersecting && v.top < bestTop) {
            bestTop = v.top;
            best = key;
          }
        });
        if (best) setActiveChapterKey(best);
      },
      { threshold: 0, rootMargin: '0px 0px -65% 0px' }
    );

    orderedChapters.forEach((ch) => {
      const key = ch.name ?? '__uncategorized__';
      const el = chapterRefs.current[key];
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, [orderedChapters, isEmbed]);

  // ── Keyboard nav: [ and ] jump between chapters ───────────────────────────
  useEffect(() => {
    if (isEmbed) return;
    const handler = (e) => {
      if (e.key !== '[' && e.key !== ']') return;
      // Don't steal keys from inputs/textareas
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      const keys = orderedChapters
        .filter((ch) => ch.name !== null)
        .map((ch) => ch.name ?? '__uncategorized__');
      const idx = keys.indexOf(activeChapterKey);
      if (e.key === '[' && idx > 0) scrollToChapter(keys[idx - 1]);
      else if (e.key === ']' && idx < keys.length - 1) scrollToChapter(keys[idx + 1]);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [orderedChapters, activeChapterKey, isEmbed]);

  // ── Derived values ────────────────────────────────────────────────────────
  const tintingEnabled = !!colors?.chapterColorTinting;
  const isPublic = timeline?.privacy === 'public';
  const showSidebar = !isEmbed && orderedChapters.length >= 2 && windowWidth >= 768;
  const showToolbar = !isEmbed && !!timeline;

  // Chapters visible to this context (embed hides empty chapters)
  const visibleChapters = useMemo(
    () =>
      isEmbed
        ? orderedChapters.filter((ch) => (byChapter.get(ch.name) || []).length > 0)
        : orderedChapters,
    [isEmbed, orderedChapters, byChapter]
  );

  const namedChapters = useMemo(
    () => visibleChapters.filter((ch) => ch.name),
    [visibleChapters]
  );

  const totalReadingTime = useMemo(() => calcReadingTime(sorted), [sorted]);

  // ── Collapse helpers ──────────────────────────────────────────────────────
  const collapseKey = (ch) => ch?.name ?? '__uncategorized__';
  const isCollapsed = (ch) => !!collapsed[collapseKey(ch)];
  const toggleChapter = useCallback(
    (ch) =>
      setCollapsed((prev) => ({ ...prev, [collapseKey(ch)]: !prev[collapseKey(ch)] })),
    []
  );
  const collapseAll = useCallback(() => {
    const s = {};
    orderedChapters.forEach((ch) => {
      s[collapseKey(ch)] = true;
    });
    setCollapsed(s);
  }, [orderedChapters]);
  const expandAll = useCallback(() => setCollapsed({}), []);
  const allCollapsed = visibleChapters.every((ch) => isCollapsed(ch));

  // ── Scroll to chapter ─────────────────────────────────────────────────────
  const scrollToChapter = useCallback((key) => {
    const el = chapterRefs.current[key];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileSheetOpen(false);
  }, []);

  // ── Chapter share link ────────────────────────────────────────────────────
  const handleShareChapter = useCallback((ch) => {
    const anchor = chapterAnchor(ch.name);
    const base = window.location.href.split('#')[0];
    const url = `${base}#${anchor}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    setCopiedChapter(ch.name);
    setTimeout(() => setCopiedChapter(null), 2000);
  }, []);

  // ── Embed state ───────────────────────────────────────────────────────────
  const embedBaseUrl = timeline?.id
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/embed/${timeline.id}`
    : '';
  const embedChapterUrl = embedChapterSel
    ? `${embedBaseUrl}#${chapterAnchor(embedChapterSel)}`
    : embedBaseUrl;
  const safeTitle = (timeline?.title || 'Timeline').replace(/"/g, '&quot;');
  const wAttr = embedResp ? '100%' : embedWidth;
  const hAttr = embedResp ? '100%' : embedHeight;
  const embedCode = `<iframe\n  src="${embedBaseUrl}"\n  width="${wAttr}"\n  height="${hAttr}"\n  style="border:none;border-radius:12px;"\n  title="${safeTitle}"\n  allowfullscreen\n></iframe>`;
  const embedChapterCode = `<iframe\n  src="${embedChapterUrl}"\n  width="${wAttr}"\n  height="${hAttr}"\n  style="border:none;border-radius:12px;"\n  title="${safeTitle}"\n  allowfullscreen\n></iframe>`;

  const copyEmbed = useCallback(() => {
    const code = embedChapterTab === 'chapter' ? embedChapterCode : embedCode;
    navigator.clipboard?.writeText(code).catch(() => {});
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  }, [embedCode, embedChapterCode, embedChapterTab]);

  // ── PNG Export ────────────────────────────────────────────────────────────
  const handleExportPNG = useCallback(async () => {
    setIsExporting(true);
    setExportMsg('Preparing export…');
    const prevCollapsed = { ...collapsed };
    const prevRevealed = revealedKeys;
    setCollapsed({});
    setRevealedKeys(new Set(orderedChapters.map((ch) => ch.name ?? '__uncategorized__')));
    await new Promise((r) => setTimeout(r, 200));
    try {
      const el = containerRef.current;
      if (!el) return;
      // Hide UI controls so they don't appear in the export
      const uiEls = el.querySelectorAll('.sl-toolbar, .sl-controls, .sl-nav-btn, .sl-end-state, .sl-share-btn');
      const prevDisplays = Array.from(uiEls).map((e) => e.style.display);
      uiEls.forEach((e) => { e.style.display = 'none'; });
      await document.fonts.ready;
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: el.scrollWidth,
        height: el.scrollHeight,
        scrollX: 0,
        scrollY: -window.scrollY,
      });
      uiEls.forEach((e, i) => { e.style.display = prevDisplays[i]; });
      const date = new Date().toISOString().slice(0, 10);
      const slug = safeSlug(timeline?.title);
      const link = document.createElement('a');
      link.download = `${slug}_${date}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert(`PNG export failed: ${err.message}`);
    } finally {
      setCollapsed(prevCollapsed);
      setRevealedKeys(prevRevealed);
      setIsExporting(false);
      setExportMsg('');
    }
  }, [collapsed, revealedKeys, orderedChapters, timeline, colors]);

  // ── PDF Export ────────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async () => {
    setIsExporting(true);
    setExportMsg('Preparing PDF…');
    const prevCollapsed = { ...collapsed };
    const prevRevealed = revealedKeys;
    setCollapsed({});
    setRevealedKeys(new Set(orderedChapters.map((ch) => ch.name ?? '__uncategorized__')));
    await new Promise((r) => setTimeout(r, 200));

    try {
      const el = containerRef.current;
      if (!el) return;

      // Hide UI controls so they don't appear in the export
      const uiEls = el.querySelectorAll('.sl-toolbar, .sl-controls, .sl-nav-btn, .sl-end-state, .sl-share-btn');
      const prevDisplays = Array.from(uiEls).map((e) => e.style.display);
      uiEls.forEach((e) => { e.style.display = 'none'; });

      setExportMsg('Rendering timeline…');
      await document.fonts.ready;

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: el.scrollWidth,
        height: el.scrollHeight,
        scrollX: 0,
        scrollY: -window.scrollY,
      });

      uiEls.forEach((e, i) => { e.style.display = prevDisplays[i]; });

      // ── A4 portrait ───────────────────────────────────────────────────────
      const pageW = 595.28;
      const pageH = 841.89;
      const MARGIN = 36;
      const FOOTER_H = 20;
      const LINE_H = 13;
      const contentW = pageW - MARGIN * 2;

      const titleText = timeline?.title || 'Storyline Timeline';
      const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

      pdf.setFontSize(10);
      const descRaw = timeline?.description ? stripHtml(timeline.description).trim() : '';
      const descLines = descRaw ? pdf.splitTextToSize(descRaw, contentW) : [];
      let HEADER = MARGIN + 28;
      if (descLines.length) HEADER += descLines.length * LINE_H + 6;
      HEADER += 12;

      const scale2x = 2;
      const natW = canvas.width / scale2x;
      const natH = canvas.height / scale2x;
      const scaleF = contentW / natW;
      const renderedW = contentW;
      const renderedH = natH * scaleF;
      const contentH = pageH - MARGIN - FOOTER_H;
      const firstPageH = contentH - HEADER;
      const totalPages = 1 + Math.max(0, Math.ceil((renderedH - firstPageH) / contentH));

      // Pre-compute chapter page positions (for bookmarks)
      const chapterPageMap = new Map();
      orderedChapters.forEach((ch) => {
        if (!ch.name) return;
        const key = ch.name ?? '__uncategorized__';
        const sectionEl = chapterRefs.current[key];
        if (!sectionEl || !el) return;
        const cRect = el.getBoundingClientRect();
        const sRect = sectionEl.getBoundingClientRect();
        const relY = (sRect.top - cRect.top) * scaleF;
        if (relY <= firstPageH) {
          chapterPageMap.set(ch.name, { page: 1, y: HEADER + relY });
        } else {
          const rem = relY - firstPageH;
          const pageIdx = Math.floor(rem / contentH);
          chapterPageMap.set(ch.name, { page: 2 + pageIdx, y: MARGIN + (rem % contentH) });
        }
      });

      const drawFooter = (pageNum) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(160, 160, 160);
        pdf.text('Chronos', MARGIN, pageH - MARGIN / 2);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pageW - MARGIN, pageH - MARGIN / 2, { align: 'right' });
      };

      // ── Page 1: title header + first canvas slice ─────────────────────────
      {
        let hy = MARGIN + 20;
        pdf.setFont('times', 'bold');
        pdf.setFontSize(20);
        pdf.setTextColor(30, 30, 30);
        pdf.text(titleText, MARGIN, hy);
        hy += 26;
        if (descLines.length) {
          pdf.setFont('times', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(80, 80, 80);
          pdf.text(descLines, MARGIN, hy);
          hy += descLines.length * LINE_H + 6;
        }
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(140, 140, 140);
        pdf.text(today, pageW - MARGIN, MARGIN + 20, { align: 'right' });

        const sliceH = Math.min(firstPageH / scaleF, natH);
        const slice = document.createElement('canvas');
        slice.width = natW * scale2x;
        slice.height = sliceH * scale2x;
        slice.getContext('2d').drawImage(canvas, 0, 0, slice.width, slice.height, 0, 0, slice.width, slice.height);
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, HEADER, renderedW, sliceH * scaleF);
        drawFooter(1);
      }

      // ── Remaining pages ───────────────────────────────────────────────────
      setExportMsg('Writing pages…');
      let renderedSoFar = firstPageH;
      let pageNum = 2;
      while (renderedSoFar < renderedH) {
        pdf.addPage();
        const srcY = renderedSoFar / scaleF;
        const srcH = Math.min(contentH / scaleF, natH - srcY);
        if (srcH <= 0) break;
        const slice = document.createElement('canvas');
        slice.width = natW * scale2x;
        slice.height = srcH * scale2x;
        slice.getContext('2d').drawImage(
          canvas, 0, srcY * scale2x, slice.width, slice.height, 0, 0, slice.width, slice.height
        );
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, MARGIN, renderedW, srcH * scaleF);
        drawFooter(pageNum);
        renderedSoFar += contentH;
        pageNum++;
      }

      // ── PDF Bookmarks (outline) ───────────────────────────────────────────
      setExportMsg('Adding bookmarks…');
      try {
        orderedChapters.forEach((ch) => {
          if (!ch.name) return;
          const info = chapterPageMap.get(ch.name);
          if (!info) return;
          const label = `${ch.icon ? ch.icon + ' ' : ''}${ch.name}`;
          pdf.outline.add(null, label, { pageNumber: info.page });
        });
      } catch (_) {
        // Outline API unavailable in this build — skip silently
      }

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`${safeSlug(titleText)}_${date}.pdf`);
    } catch (err) {
      alert(`PDF export failed: ${err.message}`);
    } finally {
      setCollapsed(prevCollapsed);
      setRevealedKeys(prevRevealed);
      setIsExporting(false);
      setExportMsg('');
    }
  }, [collapsed, revealedKeys, orderedChapters, timeline, colors]);

  // ── Early return: truly no content ────────────────────────────────────────
  if (sorted.length === 0 && normalizedChapters.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
        No events yet. Add your first event to get started.
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'relative',
      paddingRight: showSidebar && sidebarOpen ? 220 : 0,
      transition: 'padding-right 0.22s ease',
    }}>

      {/* ── Main content column ───────────────────────────────────────────── */}
      <div
        ref={containerRef}
        className="sl-container"
      >

        {/* ── Toolbar ──────────────────────────────────────────────────────── */}
        {showToolbar && (
          <div className="sl-toolbar">
            {sorted.length > 0 && (
              <span className="sl-total-read">{totalReadingTime} total</span>
            )}
            <div style={{ flex: 1 }} />
            <button className="sl-toolbar-btn" onClick={handleExportPNG} title="Export PNG">
              <Icon name="image" size={13} /> PNG
            </button>
            <button className="sl-toolbar-btn" onClick={handleExportPDF} title="Export PDF">
              <Icon name="fileText" size={13} /> PDF
            </button>
            {isPublic && (
              <button
                className="sl-toolbar-btn"
                onClick={() => setEmbedOpen(true)}
                title="Get embed code"
              >
                <Icon name="code" size={13} /> Embed
              </button>
            )}
          </div>
        )}

        {/* ── Collapse / Expand all ─────────────────────────────────────── */}
        {visibleChapters.length > 1 && (
          <div className="sl-controls" aria-label="Chapter controls">
            <button
              className="sl-control-btn"
              onClick={allCollapsed ? expandAll : collapseAll}
              aria-label={allCollapsed ? 'Expand all chapters' : 'Collapse all chapters'}
            >
              {allCollapsed ? 'Expand All' : 'Collapse All'}
            </button>
          </div>
        )}

        {/* ── Chapter sections ──────────────────────────────────────────── */}
        {visibleChapters.map((ch, i) => {
          const key = ch.name ?? '__uncategorized__';
          const chEvts = byChapter.get(ch.name) || [];
          const isEmpty = chEvts.length === 0;
          const displayName = ch.name || 'Uncategorized';
          const collapsed_ = isCollapsed(ch);
          const anchor = chapterAnchor(ch.name);

          // Color logic
          const chColor = ch.color || colors.primary;
          const textColor = ch.color ? (contrastText(ch.color) || colors.tertiary) : colors.tertiary;

          // Reveal animation
          const isRevealed = revealedKeys.has(key);

          // Nav neighbours
          const prevCh = visibleChapters[i - 1] || null;
          const nextCh = visibleChapters[i + 1] || null;

          return (
            <section
              key={key}
              id={anchor}
              ref={(el) => { chapterRefs.current[key] = el; }}
              data-chapter-key={key}
              className={`sl-chapter${isEmbed ? '' : ` sl-chapter-reveal${isRevealed ? ' sl-chapter-visible' : ''}`}`}
              aria-label={`Chapter: ${displayName}`}
            >
              {/* Divider between chapters */}
              {i > 0 && (
                <div className="sl-divider" aria-hidden="true">
                  <div className="sl-divider-line" style={{ background: `${chColor}50` }} />
                  <div
                    className="sl-divider-dot"
                    style={{ background: chColor, boxShadow: `0 0 0 5px ${chColor}20` }}
                  />
                  <div className="sl-divider-line" style={{ background: `${chColor}50` }} />
                </div>
              )}

              {/* Prev chapter nav */}
              {prevCh && !isEmbed && (
                <button
                  className="sl-nav-btn sl-nav-prev"
                  onClick={() => scrollToChapter(prevCh.name ?? '__uncategorized__')}
                  aria-label={`Go to previous chapter: ${prevCh.name || 'Uncategorized'}`}
                >
                  <Icon name="chevronL" size={13} />
                  {prevCh.icon && <span aria-hidden="true">{prevCh.icon}</span>}
                  {prevCh.name || 'Uncategorized'}
                </button>
              )}

              {/* Chapter header — collapse toggle */}
              <button
                className="sl-chapter-header"
                onClick={() => toggleChapter(ch)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleChapter(ch); }
                }}
                aria-expanded={!collapsed_}
                style={{ background: chColor }}
              >
                {ch.icon && (
                  <span className="sl-chapter-icon" aria-hidden="true">{ch.icon}</span>
                )}
                <span className="sl-chapter-name" style={{ color: textColor }}>
                  {displayName}
                </span>
                <div className="sl-chapter-meta">
                  {!isEmpty && (
                    <span className="sl-reading-time" style={{ color: textColor }}>
                      {calcReadingTime(chEvts)}
                    </span>
                  )}
                  <span
                    className="sl-chapter-count"
                    style={{ background: `${textColor}33`, color: textColor }}
                  >
                    {chEvts.length} {chEvts.length === 1 ? 'event' : 'events'}
                  </span>
                  {/* Share button — hidden in embed; always visible in editor */}
                  {!isEmbed && ch.name && (
                    <button
                      className={`sl-share-btn${canEdit ? ' sl-share-btn-always' : ''}`}
                      onClick={(e) => { e.stopPropagation(); handleShareChapter(ch); }}
                      title="Copy chapter link"
                      aria-label="Copy link to this chapter"
                    >
                      {copiedChapter === ch.name
                        ? <Icon name="check" size={12} />
                        : <Icon name="link" size={12} />}
                    </button>
                  )}
                  {/* Chevron — hide on empty chapters in non-editor views */}
                  {(!isEmpty || canEdit) && (
                    <span
                      className="sl-chapter-chevron"
                      aria-hidden="true"
                      style={{
                        color: textColor,
                        transform: collapsed_ ? 'rotate(-90deg)' : 'rotate(0deg)',
                      }}
                    >
                      ▾
                    </span>
                  )}
                </div>
              </button>

              {/* Collapsible body */}
              <div className={`sl-chapter-body${collapsed_ ? ' sl-collapsed' : ''}`}>
                <div className="sl-chapter-body-inner">

                  {/* Summary card */}
                  {ch.summary && (
                    <div
                      className="sl-summary-card"
                      style={{ borderLeftColor: ch.color || colors.secondary }}
                    >
                      <p className="sl-summary-text">{ch.summary}</p>
                      {canEdit && !isEmbed && (
                        <button className="sl-summary-edit-link" tabIndex={-1}>
                          Edit Summary in Chapters tab
                        </button>
                      )}
                    </div>
                  )}

                  {isEmpty ? (
                    /* Empty chapter state */
                    <div className="sl-empty-state">
                      <div className="sl-empty-icon" aria-hidden="true">📄</div>
                      <p className="sl-empty-msg">No events in this chapter yet.</p>
                      {canEdit && onAddEventToChapter && ch.name && (
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => onAddEventToChapter(ch.name)}
                          style={{ marginTop: 4 }}
                        >
                          Add Event to This Chapter
                        </button>
                      )}
                    </div>
                  ) : (
                    /* Event cards */
                    chEvts.map((ev, evIdx) => {
                      const tintBg = tintingEnabled && ch.color
                        ? `${ch.color}26`
                        : 'var(--surface)';
                      const tintBorder = tintingEnabled && ch.color
                        ? `${ch.color}55`
                        : 'var(--border)';
                      const leftBorderColor = tintingEnabled && ch.color ? ch.color : null;
                      // Cap stagger at card 10 (800ms max)
                      const staggerDelay = Math.min(evIdx, 10) * 80;

                      return (
                        <button
                          key={ev.id}
                          className="sl-card"
                          onClick={() => onEventClick(ev)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick(ev); }
                          }}
                          aria-label={`${ev.title}, ${formatDate(ev.startDate, ev.startEra !== 'NONE')} to ${formatDate(ev.endDate, ev.endEra !== 'NONE')}`}
                          style={{
                            background: tintBg,
                            border: `1px solid ${tintBorder}`,
                            borderLeft: leftBorderColor ? `3px solid ${leftBorderColor}` : undefined,
                            animationDelay: isEmbed ? '0ms' : `${staggerDelay}ms`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = ch.color || colors.secondary;
                            e.currentTarget.style.background = `${ch.color || colors.secondary}22`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = tintBorder;
                            e.currentTarget.style.background = tintBg;
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.outline = `2px solid ${ch.color || colors.secondary}`;
                            e.currentTarget.style.outlineOffset = '2px';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.outline = 'none';
                          }}
                        >
                          {ev.photo && (
                            <img src={ev.photo} alt="" aria-hidden="true" className="sl-card-photo" loading="lazy" />
                          )}
                          <div className="sl-card-body">
                            <h4 className="sl-card-title" style={{ color: colors.primary }}>
                              {ev.title}
                            </h4>
                            <p className="sl-card-date" style={{ color: ch.color || colors.secondary }}>
                              {formatDate(ev.startDate, ev.startEra !== 'NONE')}
                              {ev.endDate !== ev.startDate &&
                                ` → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}`}
                            </p>
                            {hasTextContent(ev.description) && (
                              <p className="sl-card-desc">{stripHtml(ev.description)}</p>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Next chapter nav */}
              {nextCh && !isEmbed && (
                <button
                  className="sl-nav-btn sl-nav-next"
                  onClick={() => scrollToChapter(nextCh.name ?? '__uncategorized__')}
                  aria-label={`Go to next chapter: ${nextCh.name || 'Uncategorized'}`}
                >
                  {nextCh.icon && <span aria-hidden="true">{nextCh.icon}</span>}
                  {nextCh.name || 'Uncategorized'}
                  <Icon name="chevronR" size={13} />
                </button>
              )}
            </section>
          );
        })}

        {/* ── End of timeline ───────────────────────────────────────────── */}
        {visibleChapters.length > 0 && (
          <div className="sl-end-state">
            <div className="sl-end-rule" style={{ background: `${colors.secondary}40` }} />
            <div className="sl-end-content">
              <div className="sl-end-icon" aria-hidden="true">🏁</div>
              <p className="sl-end-title" style={{ color: colors.primary }}>
                End of Timeline
              </p>
              {timeline?.title && (
                <p className="sl-end-sub" style={{ color: colors.secondary }}>
                  {timeline.title}
                </p>
              )}
              <button
                className="sl-back-top"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                style={{ borderColor: `${colors.secondary}60`, color: colors.secondary }}
              >
                ↑ Back to Top
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Progress sidebar (desktop) ────────────────────────────────────── */}
      {showSidebar && (
        <div className={`sl-sidebar-wrap${sidebarOpen ? '' : ' sl-sidebar-collapsed'}`}>
          <button
            className="sl-sidebar-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label={sidebarOpen ? 'Collapse chapter sidebar' : 'Expand chapter sidebar'}
            title={sidebarOpen ? 'Hide chapters' : 'Show chapters'}
          >
            {sidebarOpen ? '›' : '‹'}
          </button>
          <nav className="sl-sidebar" aria-label="Chapter navigation">
          {namedChapters.map((ch) => {
            const key = ch.name ?? '__uncategorized__';
            const chEvts = byChapter.get(ch.name) || [];
            const isActive = activeChapterKey === key;
            return (
              <button
                key={key}
                className={`sl-sidebar-item${isActive ? ' sl-sidebar-active' : ''}`}
                onClick={() => scrollToChapter(key)}
                aria-label={`Jump to chapter: ${ch.name}`}
                aria-current={isActive ? 'location' : undefined}
                style={
                  isActive
                    ? { background: `${colors.primary}1a`, color: colors.primary, borderLeftColor: colors.primary }
                    : {}
                }
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, width: '100%' }}>
                  {ch.icon && (
                    <span className="sl-sidebar-icon" aria-hidden="true">{ch.icon}</span>
                  )}
                  <span className="sl-sidebar-name">{ch.name}</span>
                </div>
                <div className="sl-sidebar-badge-row">
                  <span className="sl-sidebar-count">{chEvts.length}</span>
                  <span className="sl-sidebar-read">{calcReadingTime(chEvts)}</span>
                </div>
              </button>
            );
          })}
          </nav>
        </div>
      )}

      {/* ── Mobile chapter indicator + sheet ─────────────────────────────── */}
      {!isEmbed && visibleChapters.length >= 2 && windowWidth < 768 && (
        <>
          <button
            className="sl-mobile-chapter-btn"
            onClick={() => setMobileSheetOpen(true)}
            aria-label="Open chapter navigation"
            style={{ background: colors.primary, color: getContrastColor(colors.primary) }}
          >
            {activeChapterKey
              ? (namedChapters.find((ch) => (ch.name ?? '__uncategorized__') === activeChapterKey)
                  ?.name || 'Chapters')
              : 'Chapters'}
          </button>

          {mobileSheetOpen && (
            <div
              className="sl-mobile-sheet-overlay"
              onClick={() => setMobileSheetOpen(false)}
            >
              <div className="sl-mobile-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="sl-mobile-sheet-header">
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Chapters</span>
                  <button className="icon-btn" onClick={() => setMobileSheetOpen(false)} aria-label="Close">
                    <Icon name="close" size={16} />
                  </button>
                </div>
                {namedChapters.map((ch) => (
                  <button
                    key={ch.name}
                    className="sl-mobile-sheet-item"
                    onClick={() => scrollToChapter(ch.name)}
                    style={
                      activeChapterKey === ch.name
                        ? { color: colors.primary, fontWeight: 600 }
                        : {}
                    }
                  >
                    {ch.icon && <span aria-hidden="true">{ch.icon}</span>}
                    {ch.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Export overlay ────────────────────────────────────────────────── */}
      {isExporting && (
        <div className="sl-export-overlay" role="status" aria-label={exportMsg}>
          <div className="sl-export-card">
            <div className="export-spinner" aria-hidden="true" />
            <p className="sl-export-msg">{exportMsg}</p>
          </div>
        </div>
      )}

      {/* ── Embed modal ───────────────────────────────────────────────────── */}
      <Modal
        open={embedOpen}
        onClose={() => { setEmbedOpen(false); setEmbedCopied(false); }}
        title="Embed Storyline Timeline"
        width={580}
      >
        {/* Tabs */}
        <div className="sl-embed-tabs">
          {[['snippet', 'Embed Code'], ['chapter', 'Link to Chapter']].map(([id, label]) => (
            <button
              key={id}
              className={`sl-embed-tab${embedChapterTab === id ? ' sl-embed-tab-active' : ''}`}
              onClick={() => setEmbedChapterTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {embedChapterTab === 'snippet' ? (
          <>
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
              Paste this snippet into any webpage to embed{' '}
              <strong style={{ color: 'var(--text)' }}>{timeline?.title}</strong> as an interactive iframe.
            </p>
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ padding: '6px 14px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>HTML</span>
              </div>
              <pre style={{ padding: '14px 16px', margin: 0, fontSize: 12, lineHeight: 1.75, color: 'var(--accent2)', fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {embedCode}
              </pre>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
              Link directly to a specific chapter. Viewers will land there when the embed loads.
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ marginBottom: 6 }}>Chapter</label>
              <select
                value={embedChapterSel}
                onChange={(e) => setEmbedChapterSel(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select a chapter…</option>
                {namedChapters.map((ch) => (
                  <option key={ch.name} value={ch.name}>
                    {ch.icon ? `${ch.icon} ` : ''}{ch.name}
                  </option>
                ))}
              </select>
            </div>
            {embedChapterSel && (
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12 }}>
                <div style={{ padding: '6px 14px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>HTML</span>
                </div>
                <pre style={{ padding: '14px 16px', margin: 0, fontSize: 12, lineHeight: 1.75, color: 'var(--accent2)', fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {embedChapterCode}
                </pre>
              </div>
            )}
          </>
        )}

        {/* Size controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={embedResp}
              onChange={(e) => setEmbedResp(e.target.checked)}
              style={{ width: 'auto' }}
            />
            Responsive (100% width)
          </label>
          {!embedResp && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ marginBottom: 4 }}>Width</label>
                <input
                  value={embedWidth}
                  onChange={(e) => setEmbedWidth(e.target.value)}
                  placeholder="e.g. 900 or 100%"
                  style={{ fontSize: 13 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ marginBottom: 4 }}>Height (px)</label>
                <input
                  value={embedHeight}
                  onChange={(e) => setEmbedHeight(e.target.value)}
                  placeholder="e.g. 800"
                  style={{
                    fontSize: 13,
                    borderColor:
                      Number(embedHeight) < 500 && !embedResp ? 'var(--danger)' : '',
                  }}
                />
                {Number(embedHeight) < 500 && !embedResp && (
                  <p style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                    ⚠ Recommended minimum: 800px for Storyline timelines
                  </p>
                )}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['Small', '700', '600'], ['Medium', '900', '800'], ['Large', '1200', '1000']].map(
              ([name, w, h]) => (
                <button
                  key={name}
                  className="btn-ghost"
                  style={{ fontSize: 12, padding: '5px 12px' }}
                  onClick={() => { setEmbedWidth(w); setEmbedHeight(h); setEmbedResp(false); }}
                >
                  {name} ({w}×{h})
                </button>
              )
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            className="btn-ghost"
            onClick={() => { setEmbedOpen(false); setEmbedCopied(false); }}
          >
            Close
          </button>
          <button
            className="btn-primary"
            onClick={copyEmbed}
            style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 148 }}
          >
            <Icon name={embedCopied ? 'check' : 'copy'} size={14} />
            {embedCopied ? 'Copied!' : 'Copy Snippet'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default StorylineTimeline;
